<template>
  <b-row no-gutters>
    <b-col>
      <!-- Restyle: self-contained "Caratteristiche profilo" card, matching the efficiency-test
           "Caratteristiche test" layout (header chips, columns, orange driver bars) so the two pages
           look alike. The "Copertura profilo" counts are moved inside as a band. No FKE/peer summary
           block here — that feature is specific to efficiency tests. -->
      <div class="et-features-card">
        <div class="et-features-head" :class="{ 'is-open': isOpen }">
          <span class="et-features-title">{{ $t('scoringProfileDetails.profileFeature') }}</span>

          <div class="et-chips">
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('scoringProfileDetails.features.chipFilters') }}</span>
              <span class="et-chip-value">{{ getCountFilters }}</span>
            </span>
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('scoringProfileDetails.features.chipExcluded') }}</span>
              <span class="et-chip-value">{{ getCountExcludedProducts }}</span>
            </span>
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('scoringProfileDetails.features.chipIndicators') }}</span>
              <span class="et-chip-value">{{ getCountIndicators }}</span>
            </span>
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('scoringProfileDetails.features.chipModel') }}</span>
              <span class="et-chip-value">{{ modelChipValue }}</span>
            </span>
          </div>

          <button type="button" class="et-toggle" @click="toggleOpen">
            {{
              isOpen
                ? $t('scoringProfileDetails.features.hideDetails')
                : $t('scoringProfileDetails.features.showDetails')
            }}
            <font-awesome-icon class="et-toggle-icon" :icon="isOpen ? 'chevron-up' : 'chevron-down'" />
          </button>
        </div>

        <div v-if="isOpen" class="et-features-body">
          <div class="et-cols">
            <!-- Filtri profilo -->
            <div class="et-col">
              <div class="et-col-head">{{ $t('scoringProfileDetails.profileFilters') }}</div>
              <selected-filters :can-remove="false" :show-title="false"></selected-filters>
            </div>

            <!-- Dettagli copertura -->
            <div class="et-col">
              <div class="et-col-head">{{ $t('scoringProfileDetails.profileCoverDetails') }}</div>
              <div class="et-kv">
                <span class="et-kv-label">{{ $t('scoringProfileDetails.countExcludeProducts') }}</span>
                <span class="et-kv-value">{{ getCountExcludedProducts }}</span>
              </div>
            </div>

            <!-- Definizione di scoring -->
            <div class="et-col">
              <div class="et-col-head">
                {{ $t('scoringProfileDetails.profileScoringDefinition') }}
                <template v-if="getSelectedModelId && getSelectedModelName">
                  <span class="et-col-head-sep">·</span> {{ getSelectedModelName }}
                </template>
              </div>

              <template v-if="!getSelectedModelId && getSelectedScoringDefinition.list.length > 0">
                <div class="et-driver" v-for="(p, index) in getDriversWeightsList()" :key="'cd' + index">
                  <div class="et-driver-top">
                    <span class="et-driver-name">{{ $t(`drivers.codes.${p.driver.toUpperCase()}`) }}</span>
                    <span class="et-driver-weight">{{ $n(p.weight, 'percent') }}</span>
                  </div>
                  <div class="et-bar"><div class="et-bar-fill" :style="{ width: barWidth(p.weight) }"></div></div>
                </div>
              </template>

              <template v-else>
                <div class="et-driver" v-for="(p, index) in getSortedModelDrivers" :key="'md' + index">
                  <div class="et-driver-top">
                    <span class="et-driver-name">{{ $t(`drivers.categories.${p.driver.toUpperCase()}`) }}</span>
                    <span class="et-driver-weight">{{ $n(p.weight, 'percent') }}</span>
                  </div>
                  <div class="et-bar"><div class="et-bar-fill" :style="{ width: barWidth(p.weight) }"></div></div>
                </div>
              </template>
            </div>
          </div>

          <!-- Copertura profilo moved inside, as a band (title hidden; it is the panel's own section). -->
          <div v-if="dataCoverage" class="et-coverage">
            <profile-coverage :dataCoverage="dataCoverage" :show-title="false"></profile-coverage>
          </div>
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import SelectedFilters from './SelectedFilters.vue'
import ProfileCoverage from './ProfileCoverage.vue'
import { calculateDriversWeightsLists } from '../../utils/drivers'

export default {
  components: {
    SelectedFilters,
    ProfileCoverage,
  },
  props: {
    // "Copertura profilo" counts, shown as a band inside the panel (passed down from the details view).
    dataCoverage: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
    }
  },
  methods: {
    getDriversWeightsList() {
      return calculateDriversWeightsLists(
        null,
        this.getSelectedScoringDefinition.list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        null
      )
    },
    toggleOpen() {
      this.isOpen = !this.isOpen
    },
    // Weight (0..1) → bar width %, clamped.
    barWidth(weight) {
      return Math.min(Math.max((weight || 0) * 100, 0), 100) + '%'
    },
  },
  computed: {
    getCountIndicators() {
      return this.getSelectedModelId ? this.getSortedModelDrivers.length : this.getSelectedScoringDefinition.list.length
    },

    // One entry per applied filter: the catalog wire format never carries an entry for a filter that
    // is not applied (see scoringProfilesFilters' withFilterValue), so no emptiness check is needed.
    getCountFilters() {
      return this.getCurrentFilters.length
    },

    // Model name for the header chip, or "personalizzato" label for a custom definition.
    modelChipValue() {
      return this.getSelectedModelId
        ? this.getSelectedModelName
        : this.$t('scoringProfileDetails.customDefinition')
    },

    getCurrentFilters() {
      return this.$store.getters['scoringProfilesFilters/filtersPayload']
    },

    getSelectedScoringDefinition() {
      return this.$store.getters['scoringProfilesDefinition/getSelectedScoringDefinition']
    },

    getSequenceWeightsDecay() {
      return this.$store.getters['scoringProfilesDefinition/getSequenceWeightsDecay']
    },

    getSortedModelDrivers() {
      return this.$store.getters['scoringProfilesDefinition/getSortedModelDrivers']
    },

    getSelectedModelId() {
      return this.$store.getters['scoringProfilesDefinition/getSelectedModelId']
    },

    getSelectedModelName() {
      return this.$store.getters['scoringProfilesDefinition/getSelectedModelName']
    },

    getCountExcludedProducts() {
      return this.$store.getters['scoringProfilesCoverageDetail/getExcludedProducts'].length
    },
  },
}
</script>

<style lang="scss" scoped>
/* Restyled "Caratteristiche profilo" card — mirrors the efficiency-test "Caratteristiche test" card. */
.et-features-card {
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.et-features-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;
  background: #ccd5e2;
}
.et-features-head.is-open {
  border-bottom: 1px solid #ececf0;
}
.et-features-title {
  font-weight: 700;
  font-size: 14px;
  color: #1f2430;
  margin-right: 4px;
}
.et-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex: 1;
}
.et-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 3px 12px;
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 16px;
  font-size: 12.5px;
  white-space: nowrap;
}
.et-chip-label {
  color: #8a93a2;
}
.et-chip-value {
  font-weight: 700;
  color: #1f2430;
}

.et-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 0;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: var(--primary, #ea633e);
  cursor: pointer;
  white-space: nowrap;
}
.et-toggle-icon {
  font-size: 12px;
}

.et-features-body {
  padding: 18px 16px 16px;
  background: #fff;
}
.et-cols {
  display: flex;
  gap: 0;
}
.et-col {
  flex: 1;
  min-width: 0;
  padding: 0 22px;
  border-left: 1px solid #ececf0;
}
.et-col:first-child {
  padding-left: 0;
  border-left: none;
}
.et-col:last-child {
  padding-right: 0;
}
.et-col-head {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #99a1ad;
  margin-bottom: 12px;
}
.et-col-head-sep {
  margin: 0 1px;
}

.et-kv {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  padding: 3px 0;
}
.et-kv-value {
  font-weight: 700;
  color: #1f2430;
  font-variant-numeric: tabular-nums;
}

.et-driver {
  margin-bottom: 12px;
}
.et-driver:last-child {
  margin-bottom: 0;
}
.et-driver-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}
.et-driver-name {
  font-size: 13px;
  color: #374151;
}
.et-driver-weight {
  font-size: 13px;
  font-weight: 700;
  color: #1f2430;
  font-variant-numeric: tabular-nums;
}
.et-bar {
  height: 4px;
  border-radius: 3px;
  background: #edeef1;
  overflow: hidden;
}
.et-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--primary, #ea633e);
}

.et-coverage {
  margin-top: 18px;
}
// ProfileCoverage's .boxed is a global style; round it here so the band reads as a strip.
.et-coverage /deep/ .boxed {
  border-radius: 6px;
}
</style>
